import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import swal from "sweetalert";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
} from "../../app/itemGridPreferencesSlice";
import { appSettings } from "../../app/appSettingsSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";
import "./../../components/CustomComponents/Switchery/ToggleSwitch.css";
import "./../order/ManageOrderFilters.css";

const ManageDevices = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const pageKey = "devices";
  const defaultVisibleColumns = {
    DeviceID: true,
    Name: true,
    OS: false,
    Model: false,
    App: false,
    AppVersion: false,
    DeviceStatus: true,
    RegistrationDate: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const sortByOptions = [
    { value: "DeviceID", label: "Device ID" },
    { value: "DeviceName", label: "Device Name" },
    { value: "RegistrationDate", label: "Registration Date" },
  ];
  const defaultSortBy = { value: "DeviceID", label: "Device ID" };
  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  

  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const lastSelectedIndex = useRef(null);
  const [togglingDeviceId, setTogglingDeviceId] = useState(null);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
  }, [dispatch, pageKey]);

  const clearFilters = () => {
    setSearch("");
  };

  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        APIs.GET_ALL_DEVICES_OF_COMPANY,
        {
          params: {
            CompID,
            Search: search?.trim() || "",
            SortBy: sortBy.value,
            SortOrder: sortOrder.value,
          },
        }
      );
      const devicesData = response.data.data || response.data;
      // Devices data logged

      if (Array.isArray(devicesData)) {
        setDevices(devicesData);
      } else {
        // Unexpected API response format silently
        setDevices([]);
      }
      setLoading(false);
    } catch (error) {
      // Error fetching devices silently
      showErrorToast("Failed to load devices.");
      setLoading(false);
    }
  }, [CompID, search, sortBy, sortOrder]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchDevices();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchDevices]);

  const handleDeleteDevice = async (device) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${device.deviceName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_COMPANY_DEVICE, {
        params: {
          CompID,
          DeviceID: device.deviceID,
        },
      });

      if (response.data.status) {
        setDevices((prevDevices) =>
          prevDevices.filter(
            (delDevice) => delDevice.deviceID !== device.deviceID
          )
        );
        showSuccessToast("Device deleted successfully.");
      } else {
        showErrorToast(response.data.message || "Deletion failed.");
      }
      setLoading(false);
    } catch (err) {
      // Error deleting device silently
      showErrorToast("Something went wrong while deleting the device.");
      setLoading(false);
    }
  };

  const handleSelectItem = (deviceId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (devices[i]?.deviceID) {
            newSelected.add(devices[i].deviceID);
          }
        }
      } else {
        if (newSelected.has(deviceId)) {
          newSelected.delete(deviceId);
        } else {
          newSelected.add(deviceId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(devices.map((item) => item.deviceID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No devices selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected devices?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((deviceId) =>
          axiosInstance.delete(APIs.DELETE_COMPANY_DEVICE, {
            params: {
              CompID,
              DeviceID: deviceId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchDevices();
      showSuccessToast(`${selectedItems.size} devices deleted successfully.`);
      setLoading(false);
    } catch (err) {
      // Error in bulk delete silently
      showErrorToast("Failed to delete selected devices.");
      setLoading(false);
    }
  };

  /** Device activeness: use 1 for active, 0 for inactive everywhere. */
  const isActiveStatus = (status) => {
    if (status == null || status === "") return false;
    const s = String(status).trim();
    return s === "1" || s === 1;
  };

  const toggleItemStatus = async (DeviceID, currentStatus) => {
    if (togglingDeviceId === DeviceID) return;
    setTogglingDeviceId(DeviceID);

    const currentlyActive = isActiveStatus(currentStatus);
    const newActive = !currentlyActive;
    const newStatus = newActive ? "1" : "0";

    try {
      const updatedItems = devices.map((item) =>
        item.deviceID === DeviceID ? { ...item, deviceStatus: newStatus } : item
      );
      setDevices(updatedItems);

      await axiosInstance.patch(APIs.PATCH_UPDATE_DEVICE_STATUS, {
        CompID,
        DeviceID: DeviceID,
        Status: newActive,
      });
      showSuccessToast(`Device ${newActive ? "enabled" : "disabled"} successfully.`);
    } catch (error) {
      const revertedItems = devices.map((item) =>
        item.deviceID === DeviceID
          ? { ...item, deviceStatus: currentStatus }
          : item
      );
      setDevices(revertedItems);
      showErrorToast("Failed to update device status.");
    } finally {
      setTogglingDeviceId(null);
    }
  };

  const renderNoDataMessage = () => {
    if (search.trim() && devices.length === 0) {
      return (
        <div className="alert alert-info text-center">
          No devices found matching your search criteria.
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Devices</li>
          </ol>
          <h1 className="page-header mb-0">Manage Devices</h1>
        </div>
      </div>

      <div className="mb-3 mt-2">

        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              {/* Enhanced Filter Section */}
              <div className="mb-4">
                <div
                  className="card filter-card"
                  style={{
                    position: "relative",
                    zIndex: 1,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: `0 4px 20px ${themeColor}40`
                  }}
                >
                  <div className="card-body p-3">
                    {/* Filter Controls */}
                    <div className="row g-3 align-items-center">
                      {/* Search Field */}
                      <div className="col-xl-4 col-lg-4 col-md-6">
                        <div className="search-input filter-control">
                          <input
                            type="text"
                            className="filter-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by device name, ID, OS..."
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              color: '#333'
                            }}
                          />
                        </div>
                      </div>

                      {/* Sort By Dropdown */}
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={sortBy}
                            onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                            options={sortByOptions}
                            styles={dropdownStyles}
                            placeholder="Sort By"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>

                      {/* Sort Order Dropdown */}
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <SortOrderDropDownSelection
                            defaultValue={sortOrder}
                            onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="col-xl-3 col-lg-3 col-md-12">
                        <div className="d-flex gap-2">
                          <button
                            className="clear-btn"
                            onClick={clearFilters}
                            title="Clear all filters"
                          >
                            <i className="fa fa-times me-1"></i>
                            Filter
                          </button>

                          <ColumnSelection
                            visibleColumns={visibleColumns}
                            setVisibleColumns={(cols) =>
                              dispatch(setVisibleColumns({ pageKey, columns: cols }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Selection Info and Actions (Shown only when devices are selected) */}
              {selectedItems.size > 0 && (
                <div className="mb-3">
                  <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} device{selectedItems.size > 1 ? 's' : ''} selected
                      </span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={handleBulkDelete}
                        title="Delete Selected Devices"
                      >
                        <i className="fa fa-trash me-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {renderNoDataMessage()}

              <div
                className="table-responsive mt-3"
                style={{
                  minHeight: "200px",
                  maxHeight: "450px",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table table-hover">
                  <thead
                    className="bg-light"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 5,
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      <th className="pt-2 pb-2">
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={
                            devices.length > 0 &&
                            selectedItems.size === devices.length
                          }
                        />
                      </th>
                      {visibleColumns.Name && (
                        <th className="pt-2 pb-2 text-center">Name</th>
                      )}

                      {visibleColumns.DeviceID && (
                        <th className="pt-2 pb-2 text-center">Device ID</th>
                      )}
                      {visibleColumns.OS && (
                        <th className="pt-2 pb-2 text-center">OS</th>
                      )}
                      {visibleColumns.Model && (
                        <th className="pt-2 pb-2 text-center">Model</th>
                      )}
                      {visibleColumns.App && (
                        <th className="pt-2 pb-2 text-center">App</th>
                      )}
                      {visibleColumns.AppVersion && (
                        <th className="pt-2 pb-2 text-center">App Version</th>
                      )}
                      {visibleColumns.DeviceStatus && (
                        <th className="pt-2 pb-2 text-center">Status</th>
                      )}
                      {visibleColumns.RegistrationDate && (
                        <th className="pt-2 pb-2 text-center">
                          Registration Date
                        </th>
                      )}
                      <th className="pt-2 pb-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((device, index) => (
                      <tr key={device.deviceID}>
                        <td className="align-middle">
                          <input
                            type="checkbox"
                            checked={selectedItems.has(device.deviceID)}
                            onChange={(e) =>
                              handleSelectItem(device.deviceID, index, e)
                            }
                            data-chkbox-shiftsel="type1"
                          />
                        </td>
                        {visibleColumns.Name && (
                          <td className="align-middle text-center">
                            {device.deviceName}
                          </td>
                        )}
                        {visibleColumns.DeviceID && (
                          <td className="align-middle text-center">
                            {device.deviceID}
                          </td>
                        )}
                        {visibleColumns.OS && (
                          <td className="align-middle text-center">
                            {device.os}
                          </td>
                        )}
                        {visibleColumns.Model && (
                          <td className="align-middle text-center">
                            {device.model}
                          </td>
                        )}
                        {visibleColumns.App && (
                          <td className="align-middle text-center">
                            {device.app}
                          </td>
                        )}
                        {visibleColumns.AppVersion && (
                          <td className="align-middle text-center">
                            {device.appVersion}
                          </td>
                        )}
                        {visibleColumns.DeviceStatus && (
                          <td className="align-middle text-center">
                            <div
                              className={`toggle-switch ${isActiveStatus(device.deviceStatus) ? "active" : ""}`}
                            >
                              <label className="switch">
                                <input
                                  type="checkbox"
                                  checked={isActiveStatus(device.deviceStatus)}
                                  disabled={togglingDeviceId === device.deviceID}
                                  onChange={() =>
                                    toggleItemStatus(
                                      device.deviceID,
                                      device.deviceStatus
                                    )
                                  }
                                />
                                <span
                                  className="slider round"
                                  style={{
                                    backgroundColor: isActiveStatus(device.deviceStatus) ? themeColor : '#ccc'
                                  }}
                                ></span>
                              </label>
                            </div>
                          </td>
                        )}
                        {visibleColumns.RegistrationDate && (
                          <td className="align-middle text-center">
                            {new Date(
                              device.registrationDate
                            ).toLocaleString()}
                          </td>
                        )}
                        <td className="align-middle text-center">
                          <div className="d-flex justify-content-center gap-2">
                            {/* Delete Icon */}
                            <button
                              onClick={() => handleDeleteDevice(device)}
                              className="btn btn-sm btn-outline-danger"
                              title="Delete Device"
                              style={{
                                width: '32px',
                                height: '32px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '6px',
                                border: '1px solid #dc3545',
                                color: '#dc3545',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#dc3545';
                                e.currentTarget.style.color = 'white';
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#dc3545';
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                            >
                              <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ManageDevices;
